import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  accessLevelToHeaders,
  getRequestHttpParams,
  getUrlFromRequest,
  impersonationToHeaders,
  parseGetListRequestParams,
  parseGetRequestParams,
} from '@app/core/utilities/api.utilities';
import {
  arraySnakeToCamelCase,
  objectSnakeToCamelCase,
  trimValue,
  valueToCamelCase,
  valueToSnake,
} from '@app/core/utilities/string.utilities';
import {
  ApiDeleteRequest,
  ApiDeleteResponse,
  ApiErrorResponse,
  ApiGetListRequest,
  ApiGetListResponse,
  ApiGetObjectRequest,
  ApiGetObjectResponse,
  ApiPatchRequest,
  ApiPatchResponse,
  ApiPostRequest,
  ApiPostResponse,
  ApiPutRequest,
  ApiPutResponse,
  ApiRequest,
  ApiResponse,
  CustomerApiRoute,
} from '../models/api.models';
import { LoggingService } from '../services/error-logging.service';

const excludeLogsForRoutesWithStatus = [{ url: CustomerApiRoute.Plan, status: [404] }];
const logAsInfoRoutes = [];

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private http: HttpClient,
    private ls: LoggingService,
  ) {}

  getList<T extends object, U extends object = T>(request: ApiGetListRequest<T>): Observable<ApiGetListResponse<U>> {
    request.params = parseGetListRequestParams(request);

    return this.request<T, ApiGetListResponse<U>>({ ...request, method: 'GET' }).pipe(
      map((response) => ({
        ...response,
        data: request.snakeCase ? response.data || [] : arraySnakeToCamelCase(response.data || []),
      })),
    );
  }

  getObject<T extends object, U extends object = T>(
    request: ApiGetObjectRequest<T>,
  ): Observable<ApiGetObjectResponse<U>> {
    request.params = parseGetRequestParams(request);

    return this.request<T, ApiGetObjectResponse<U>>({ ...request, method: 'GET' }).pipe(
      map((response) => ({ ...response, data: response.data && objectSnakeToCamelCase(response.data) })),
    );
  }

  post<T extends object>(request: ApiPostRequest<T>): Observable<ApiPostResponse<T>> {
    return this.request<T, ApiPostResponse<T>>({ ...request, method: 'POST' }, request.data).pipe(
      map((response) => ({ ...response, data: response.data && objectSnakeToCamelCase(response.data) })),
    );
  }

  put<T extends object>(request: ApiPutRequest<T>): Observable<ApiPutResponse<T>> {
    return this.request<T, ApiPutResponse<T>>({ ...request, method: 'PUT' }, request.data).pipe(
      map((response) => ({ ...response, data: response.data && valueToCamelCase(response.data) })),
    );
  }

  patch<T extends object>(request: ApiPatchRequest<T>): Observable<ApiPatchResponse<T>> {
    return this.request<T, ApiPatchResponse<T>>({ ...request, method: 'PATCH' }, request.data).pipe(
      map((response) => ({ ...response, data: response.data && objectSnakeToCamelCase(response.data) })),
    );
  }

  delete<T extends object>(request: ApiDeleteRequest<T>): Observable<ApiDeleteResponse> {
    return this.request({ ...request, method: 'DELETE' }, request.data);
  }

  private request<T extends object, U extends ApiResponse = ApiResponse>(
    request: ApiRequest,
    body?: Partial<T>,
  ): Observable<U> {
    if (body) {
      body = trimValue(valueToSnake(body));
    }

    const headers = impersonationToHeaders(request.impersonationId, accessLevelToHeaders(request.accessLevel));
    const observe = (request.observe || 'body') as 'body';

    return this.http
      .request<U>(request.method, getUrlFromRequest(request), {
        body,
        params: getRequestHttpParams(request.params),
        responseType: 'json',
        headers,
        observe,
      })
      .pipe(catchError((error) => this.handleError(request, error)));
  }

  private handleError(request: ApiRequest, err): Observable<never> {
    const error = err?.original?.error;
    const toBeLogged = !excludeLogsForRoutesWithStatus.find(
      (d) => (<string[]>request.route)?.includes(d.url) && d.status?.includes(error.status),
    );

    if (toBeLogged) {
      const errorType = !logAsInfoRoutes.find((d) => (<string[]>request.route).includes(d)) ? 'error' : 'info';

      this.ls.logError(
        `Failed to ${request.method} ${request.endpoint}/${
          Array.isArray(request.route) ? request.route.join('/') : request.route.toString()
        }`,
        err,
        errorType,
      );
    }
    const response: ApiErrorResponse = { status: error.status, message: error, errors: error.errors };

    return throwError(response);
  }
}
